import React, { useState, useEffect } from 'react';
import ChatIcon from './components/ChatIcon';
import { fetchPredefinedQuestions } from './services/predefinedQuestionsService';
import hybridChatService from './services/hybridChatService';
import { API_ENDPOINTS } from './config/api';

// Renamed legacy component: PulseEmbeddedDemo (formerly PulseEmbeddedOld)
const PulseEmbeddedDemo = ({ userInfo, domainId }) => {
  // Fallback: if no userInfo prop passed, attempt to load from localStorage (Okta profile persisted there)
  let effectiveUserInfo = userInfo;
  if (!effectiveUserInfo || Object.keys(effectiveUserInfo).length === 0) {
    try {
      effectiveUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    } catch {
      effectiveUserInfo = {};
    }
  }
  const [searchQuery, setSearchQuery] = useState('');
  const [currentQuestionSet, setCurrentQuestionSet] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [allThreads, setAllThreads] = useState([]);
  const [showAboutBox, setShowAboutBox] = useState(false);

  // Predefined questions - fetched from API or fallback to defaults
  const [allQuestions, setAllQuestions] = useState([
    'Share the latest company updates on AI developments',
    'What required learning do I have?',
    'Whose in the office today?',
    'What are the upcoming project deadlines?',
    'Show me my recent performance reviews',
    'What are the current company policies?',
    'Find available meeting rooms for today',
    'What are the latest HR announcements?',
    'Show me my vacation balance and requests'
  ]);

  // Helper: resolve domainId from query param first, else props/local user
  const resolveDomainId = () => {
    try {
      const usp = new URLSearchParams(window.location.search);
      const qd = usp.get('domainId');
      if (qd) return qd.toUpperCase();
    } catch {}
    const id = domainId || effectiveUserInfo?.domainId || effectiveUserInfo?.domain_id || null;
    return id ? String(id).toUpperCase() : null;
  };

  // Helper: resolve user name from query param first, else props/local user
  const resolveUserName = () => {
    try {
      const usp = new URLSearchParams(window.location.search);
      const un = usp.get('userName');
      if (un) return un;
    } catch {}
    return (
      effectiveUserInfo?.given_name ||
      effectiveUserInfo?.firstName ||
      (effectiveUserInfo?.name?.split(' ')[0]) ||
      null
    );
  };

  // Fetch predefined questions from API on component mount
  useEffect(() => {
    const loadPredefinedQuestions = async () => {
      const effectiveDomainId = resolveDomainId();
      if (!effectiveDomainId) {
        console.warn('⚠️ No domainId available for predefined questions; skipping fetch');
        return;
      }
      const questions = await fetchPredefinedQuestions(effectiveDomainId);
      if (questions && questions.length > 0) {
        setAllQuestions(questions);
      }
    };
    loadPredefinedQuestions();
  }, [domainId, effectiveUserInfo?.domainId, effectiveUserInfo?.domain_id]);

  // Detect iframe context (same-origin safe fallback)
  const isInIframe = () => {
    try {
      return window !== window.parent;
    } catch (e) {
      return true; // Cross-origin -> treat as iframe
    }
  };

  // Dynamic hidden form submission (uses actual window/parent origin, no hardcoded host)
  const handleFormSubmission = (query, type = 'manual', additionalData = {}) => {
    if (!isInIframe()) {
      return false; // Outside iframe: caller will do normal navigation
    }

    console.group('🚀 DYNAMIC FORM SUBMISSION STARTED (PulseEmbeddedDemo)');
    console.log('📝 Params:', { query, type, additionalData });

    const form = document.createElement('form');
    form.method = 'GET';
    // Determine target origin: prefer parent window origin if accessible, else referrer, else current origin
    let targetOrigin = window.location.origin;
    try {
      // Same-origin parent available
      if (window.parent && window.parent.location && window.parent.location.origin) {
        targetOrigin = window.parent.location.origin;
      } else if (document.referrer) {
        targetOrigin = new URL(document.referrer).origin;
      }
    } catch (_) {
      // Cross-origin access blocked; fall back to referrer or current origin
      try {
        if (document.referrer) targetOrigin = new URL(document.referrer).origin;
      } catch (__) {
        targetOrigin = window.location.origin;
      }
    }

    form.action = `${targetOrigin}/resultpage`;
    form.target = '_top';
    form.style.display = 'none';

    // Use top-level parent URL directly when accessible; fallback to referrer/current URL
    let parentUrl = window.location.href;
    try {
      if (window.parent && window.parent.location && window.parent.location.href) {
        parentUrl = window.parent.location.href;
      } else if (document.referrer) {
        parentUrl = document.referrer;
      }
    } catch (_) {
      parentUrl = document.referrer || window.location.href;
    }

    const formData = {
      query,
      type,
      ...additionalData,
      fromIframe: 'true',
      parentUrl,
      selfUrl: window.location.href
    };

    Object.keys(formData).forEach(key => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = formData[key];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    console.log('✅ Hidden form appended. Submitting in 2s...');
    setTimeout(() => {
      console.log('🚀 Submitting form now - navigating to results page!');
      form.submit();
      console.groupEnd();
    }, 2000);
    return true;
  };

  const showDropdownInParent = (suggestions, inputRect) => {
    if (isInIframe() && window.parent) {
      try {
        setTimeout(() => {}, 100);
        window.parent.postMessage({
          type: 'PULSE_SHOW_DROPDOWN',
          data: {
            suggestions,
            position: { x: inputRect.left, y: inputRect.bottom, width: inputRect.width },
            iframeId: 'pulseIframe'
          }
        }, '*');
      } catch (error) {
        console.error('Cannot send message to parent:', error);
        setShowSuggestions(true);
      }
    }
  };

  const hideDropdownInParent = () => {
    if (isInIframe() && window.parent) {
      try {
        window.parent.postMessage({ type: 'PULSE_HIDE_DROPDOWN' }, '*');
      } catch (error) {
        console.error('Cannot send message to parent:', error);
      }
    }
  };

  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event?.data?.type === 'PULSE_DROPDOWN_SELECTION') {
        const suggestion = event.data.data;
        handleSuggestionClick(suggestion);
        return;
      } else if (event?.data?.type === 'PULSE_INTEGRATION_SUPPORTED') {
        setParentSupportsBreakout(true);
      }
      // Removed parent URL capture via postMessage: we now rely on window.parent.location/top
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const [parentSupportsBreakout, setParentSupportsBreakout] = React.useState(false);

  const navigateToResults = (question, conversationId = null, type = null) => {
    // Attempt iframe breakout via hidden form first
    if (handleFormSubmission(question, type || 'manual', {
      conversationId: conversationId || '',
      fromPulseEmbeddedDemo: 'true'
    })) {
      return; // Successful form submission
    }

    const params = new URLSearchParams({
      query: question,
      ...(conversationId && { conversationId }),
      ...(type && { type }),
      fromIframe: 'false',
      parentUrl: (typeof window !== 'undefined' && window.__PULSE_PARENT_URL) || 'none'
    });
    const resultUrl = `/resultpage?${params.toString()}`;
    window.location.href = resultUrl;
  };

  const loadThreadsFromStorage = async () => {
    try {
      try {
        const effectiveDomainId = resolveDomainId();
        if (!effectiveDomainId) {
          console.warn('⚠️ No domainId available to load threads; skipping remote fetch');
          throw new Error('missing_domain_id');
        }
        const apiUrl = API_ENDPOINTS.USER_CONVERSATIONS(effectiveDomainId) + `?limit=10`;
        const response = await fetch(apiUrl, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
        if (response.ok) {
          const data = await response.json();
          let conversations = data;
          if (data && data.conversations) conversations = data.conversations;
          else if (data && data.data) conversations = data.data;
          if (Array.isArray(conversations) && conversations.length > 0) {
            return conversations.map(conv => ({
              id: conv.id,
              title: conv.title || 'Untitled Conversation',
              conversation: conv.messages ? conv.messages.map(msg => ({
                type: msg.message_type,
                text: msg.content
              })) : []
            }));
          }
        } else {
          const errorText = await response.text();
          console.error('❌ Direct API failed:', response.status, errorText);
        }
      } catch (directAPIError) {
        console.error('❌ Direct API call failed:', directAPIError);
      }

      // Hybrid service fallback
      try {
        const effectiveDomainId2 = resolveDomainId();
        if (!effectiveDomainId2) {
          console.warn('⚠️ No domainId for hybridChatService; skipping');
          return [];
        }
        hybridChatService.setUserId(effectiveDomainId2);
        const conversations = await hybridChatService.getConversationHistory(10);
        if (conversations && conversations.length > 0) {
          return conversations.map(conv => ({
            id: conv.id,
            title: conv.title || 'Untitled Conversation',
            conversation: conv.messages ? conv.messages.map(msg => ({
              type: msg.message_type,
              text: msg.content
            })) : []
          }));
        }
      } catch (hybridError) {
        console.error('❌ Hybrid service fallback failed:', hybridError);
      }

      return [];
    } catch (error) {
      console.error('❌ Error loading threads from API:', error);
      return [];
    }
  };

  React.useEffect(() => {
    const loadThreads = async () => {
      try {
        const threads = await loadThreadsFromStorage();
        setAllThreads(threads || []);
      } catch (error) {
        console.error('Error loading threads:', error);
        setAllThreads([]);
      }
    };
    loadThreads();
  }, []);

  const getFilteredSuggestions = (query) => {
    // Exclude placeholder threads titled "New Chat" from dropdown
    const threadsRaw = Array.isArray(allThreads) ? allThreads : [];
    const threads = threadsRaw.filter(t => t?.title && t.title.trim() !== 'New Chat');

    // If no data from API/hybrid, show placeholder entry
    if (threads.length === 0) {
      return [{ id: 'no-prev', title: 'No previous conversations', isPlaceholder: true }];
    }

    if (!query.trim()) {
      const maxSuggestions = isInIframe() ? 3 : 6;
      return threads.slice(0, maxSuggestions);
    }
    const filtered = threads.filter(thread =>
      thread.title && thread.title.toLowerCase().includes(query.toLowerCase())
    );
    const maxSuggestions = isInIframe() ? 3 : 6;
    return filtered.slice(0, maxSuggestions);
  };

  const handleInputFocus = () => {
    const currentSuggestions = getFilteredSuggestions(searchQuery);
    setSuggestions(currentSuggestions);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    const filteredSuggestions = getFilteredSuggestions(value);
    setSuggestions(filteredSuggestions);
    if (filteredSuggestions.length > 0) {
      if (isInIframe()) {
        const inputElement = e.target;
        const rect = inputElement.getBoundingClientRect();
        showDropdownInParent(filteredSuggestions, rect);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(true);
      }
    } else {
      if (isInIframe()) {
        hideDropdownInParent();
        setShowSuggestions(false);
      } else {
        setShowSuggestions(false);
      }
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Ignore placeholder item
    if (suggestion?.isPlaceholder || suggestion?.id === 'no-prev') {
      setShowSuggestions(false);
      if (isInIframe()) hideDropdownInParent();
      return;
    }

    if (isInIframe()) {
      hideDropdownInParent();
    } else {
      setShowSuggestions(false);
    }

    if (typeof suggestion === 'string') {
      if (handleFormSubmission(suggestion, 'predefined', { fromSuggestion: 'true' })) {
        setSearchQuery('');
        return;
      }
      setSearchQuery('');
      navigateToResults(suggestion, null, 'predefined');
    } else {
      if (handleFormSubmission(suggestion.title, 'thread', {
        conversationId: suggestion.id,
        fromThread: 'true'
      })) {
        setSearchQuery('');
        return;
      }
      setSearchQuery(suggestion.title);
      navigateToResults(suggestion.title, suggestion.id, 'thread');
    }
  };

  const handleInputClick = () => {
    if (showSuggestions) {
      if (isInIframe()) {
        hideDropdownInParent();
      }
      setShowSuggestions(false);
    } else {
      const currentSuggestions = getFilteredSuggestions(searchQuery);
      setSuggestions(currentSuggestions);
      if (currentSuggestions.length > 0) {
        if (isInIframe()) {
          const inputElement = document.querySelector('input[type="text"]');
          if (inputElement) {
            const rect = inputElement.getBoundingClientRect();
            showDropdownInParent(currentSuggestions, rect);
          }
          setTimeout(() => {
            setShowSuggestions(true);
          }, 150);
        } else {
          setShowSuggestions(true);
        }
      }
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (isInIframe()) {
        hideDropdownInParent();
      } else {
        setShowSuggestions(false);
      }
    }, 200);
  };

  const getCurrentQuestions = () => {
    const startIndex = currentQuestionSet * 3;
    return allQuestions.slice(startIndex, startIndex + 3);
  };

  const handleRefresh = () => {
    setCurrentQuestionSet((prev) => (prev + 1) % 3);
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    const query = searchQuery.trim();
    if (handleFormSubmission(query, 'manual', { fromSearch: 'true' })) {
      setSearchQuery('');
      return;
    }
    navigateToResults(query, null, 'manual');
    setSearchQuery('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div 
      className="min-h-screen w-full relative"
      style={{
        background: 'linear-gradient(115deg, #122F65 2.06%, #00123C 97.35%)',
        padding: 'clamp(16px, 4vw, 32px) clamp(16px, 10vw, 128px) 24px clamp(16px, 10vw, 128px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: '24px'
      }}
    >
      <svg 
        className="absolute" 
        style={{
          width: '1437px',
          height: '697px',
          top: '-474px',
          left: '0',
          pointerEvents: 'none'
        }} 
        width="1437" 
        height="223" 
        viewBox="0 0 1437 223" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <ellipse cx="344.94" cy="-29" rx="344.94" ry="252" fill="url(#paint0_radial_6199_120697)"/>
        <ellipse cx="665.278" cy="-72.5" rx="352.974" ry="257.5" fill="url(#paint1_radial_6199_120697)"/>
        <ellipse cx="984.106" cy="-121" rx="263.099" ry="276" fill="url(#paint2_radial_6199_120697)"/>
        <ellipse cx="1203.53" cy="-127" rx="233.475" ry="347" fill="url(#paint3_radial_6199_120697)"/>
        <defs>
          <radialGradient id="paint0_radial_6199_120697" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(344.94 -29) rotate(90) scale(252 344.94)">
            <stop stopColor="#44B8F3" stopOpacity="0.4"/>
            <stop offset="1" stopColor="#44B8F3" stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="paint1_radial_6199_120697" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(665.278 -72.5) rotate(90) scale(257.5 352.974)">
            <stop stopColor="#44F3B3" stopOpacity="0.4"/>
            <stop offset="1" stopColor="#44F3B3" stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="paint2_radial_6199_120697" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(984.106 -121) rotate(90) scale(276 263.099)">
            <stop stopColor="#F8D666" stopOpacity="0.4"/>
            <stop offset="1" stopColor="#F8D666" stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="paint3_radial_6199_120697" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(1203.53 -127) rotate(90) scale(347 233.475)">
            <stop stopColor="#E3725F" stopOpacity="0.4"/>
            <stop offset="1" stopColor="#E3725F" stopOpacity="0"/>
          </radialGradient>
        </defs>
      </svg>

      <div className="relative w-full" style={{ zIndex: 10 }}>
        <div className="flex justify-between items-center mb-6">
          <div 
            className="text-white"
            style={{
              fontFamily: 'Elevance Sans, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '24px',
              fontWeight: '400',
              lineHeight: '150%'
            }}
          >
            Welcome, {resolveUserName() || 'User'}!
          </div>
        </div>

        <div className="flex flex-col items-start gap-2.5 w-full max-w-[1184px] mx-auto relative">
          <div 
            className="flex items-center justify-between w-full px-4 py-3 gap-2"
            style={{
              height: '56px',
              borderRadius: '999px',
              border: '2px solid #2861BB',
              background: 'linear-gradient(115deg, #122F65 2.06%, #00123C 97.35%)'
            }}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M32.0003 15.9847C24.175 18.052 18.0679 24.1708 16.0156 32C13.9483 24.1748 7.8295 18.0677 0.000272721 16.0154C7.82554 13.9481 13.9326 7.82927 15.9849 4.3842e-05C18.0522 7.82532 24.171 13.9324 32.0003 15.9847Z" fill="url(#paint0_linear_6199_120715)"/>
                <defs>
                  <linearGradient id="paint0_linear_6199_120715" x1="24.9996" y1="15.3216" x2="24.3843" y2="6.17172" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#44B8F3"/>
                    <stop offset="1" stopColor="#7BFFFE"/>
                  </linearGradient>
                </defs>
              </svg>
              <input
                type="text"
                placeholder="What do you need to find today?"
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onClick={handleInputClick}
                onBlur={handleInputBlur}
                onKeyPress={handleKeyPress}
                className="bg-transparent border-none outline-none flex-1 min-w-0"
                style={{
                  fontFamily: 'Elevance Sans, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '20px',
                  fontWeight: '400',
                  lineHeight: '150%',
                  width: 'auto',
                  maxWidth: '100%',
                  overflow: 'visible',
                  color: 'white',
                  '::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </div>
            <div className="flex items-center gap-0 px-1 flex-shrink-0">
              <button 
                onClick={handleSearch}
                className="flex items-center justify-center p-2 hover:opacity-80 transition-opacity"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M1.33333 0C0.596954 0 0 0.596954 0 1.33333V5.33333C0.000889163 5.8993 0.358985 6.40303 0.893229 6.58984C1.03457 6.64002 1.18336 6.66599 1.33333 6.66667C1.33811 6.66669 1.34288 6.66669 1.34766 6.66667L9.33333 8L1.34766 9.33463C1.34288 9.33418 1.33811 9.33374 1.33333 9.33333C1.18292 9.33386 1.03368 9.35983 0.891927 9.41016C0.358196 9.59741 0.000695057 10.101 0 10.6667V14.6667C0 15.403 0.596954 16 1.33333 16C1.56548 15.9996 1.79348 15.9385 1.99479 15.8229H1.99609L15.5938 8.61458L15.5951 8.61198C15.8403 8.50731 15.9995 8.26661 16 8C16.0004 7.73213 15.8403 7.49006 15.5938 7.38542L1.99609 0.177083C1.7944 0.0612606 1.56592 0.000212161 1.33333 0Z" fill="white"/>
                </svg>
              </button>
              <button className="flex items-center justify-center p-2 hover:opacity-80 transition-opacity">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.9989 14C13.6776 14 15.0328 12.66 15.0328 11V5C15.0328 3.34 13.6776 2 11.9989 2C10.3202 2 8.9651 3.34 8.9651 5V11C8.9651 12.66 10.3202 14 11.9989 14ZM17.9756 11C17.48 11 17.0654 11.36 16.9845 11.85C16.5699 14.2 14.4968 16 11.9989 16C9.50107 16 7.42796 14.2 7.01333 11.85C6.93243 11.36 6.51781 11 6.02228 11C5.40541 11 4.91999 11.54 5.01101 12.14C5.50653 15.14 7.93359 17.49 10.9876 17.92V20C10.9876 20.55 11.4427 21 11.9989 21C12.5551 21 13.0102 20.55 13.0102 20V17.92C16.0643 17.49 18.4913 15.14 18.9868 12.14C19.088 11.54 18.5924 11 17.9756 11Z" fill="white" fillOpacity="0.3"/>
                </svg>
              </button>
            </div>
          </div>

          {showSuggestions && suggestions.length > 0 && (
            <div 
              className="absolute left-0 right-0 scrollbar-hide"
              style={{
                top: 'calc(100% + 8px)',
                width: '100%',
                maxWidth: '1184px',
                maxHeight: isInIframe() ? '180px' : '320px',
                borderRadius: '16px',
                border: '2px solid #2861BB',
                background: 'linear-gradient(115deg, #122F65 2.06%, #00123C 97.35%)',
                padding: '12px',
                boxShadow: '0px 8px 24px 0px rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(10px)',
                zIndex: 9999,
                overflowY: 'auto'
              }}
            >
              <div 
                style={{
                  maxHeight: isInIframe() ? '160px' : '300px',
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
                className="scrollbar-hide"
              >
                {suggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.id}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="flex items-center hover:bg-white/10 cursor-pointer transition-all duration-200"
                    style={{
                      padding: isInIframe() ? '8px 4px' : '8px 4px',
                      marginBottom: index < suggestions.length - 1 ? '2px' : '0',
                      minHeight: isInIframe() ? '32px' : '32px'
                    }}
                  >
                    <div className="w-3 h-3 mr-2 flex-shrink-0">
                      <ChatIcon className="w-3 h-3" color="#87D2F7" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p 
                        className="text-white font-medium truncate text-left" 
                        style={{ 
                          fontSize: isInIframe() ? '13px' : '14px', 
                          lineHeight: isInIframe() ? '18px' : '18px',
                          fontFamily: 'Elevance Sans, -apple-system, Roboto, Helvetica, sans-serif',
                          textAlign: 'left'
                        }}
                      >
                        {suggestion.title}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col justify-center items-start gap-2 w-full max-w-[1184px] mx-auto mt-6">
          <div className="flex justify-between items-center w-full">
            <button
              onClick={handleRefresh}
              className="flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
              style={{
                width: '45px',
                height: '45px',
                padding: '12px 16px',
                borderRadius: '100px',
                border: '1px solid #44B8F3',
                background: 'rgba(5, 15, 38, 0.40)'
              }}
            >
              <div className="w-6 h-6 flex-shrink-0 relative">
                <svg 
                  className="absolute" 
                  style={{ width: '17px', height: '24px', left: '4px', top: '0' }} 
                  width="17" 
                  height="24" 
                  viewBox="0 0 17 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path fillRule="evenodd" clipRule="evenodd" d="M16.2297 8.46742C16.741 9.50225 17 10.7056 17 12C17 16.7461 13.175 20.6292 8.5 20.6292C8.33398 20.6292 8.16133 20.6124 7.99531 20.6022L10.1203 22.7865L8.925 24L5.33906 20.3865L4.75469 19.7663L5.33906 19.1461L8.925 15.5326L10.1203 16.7461L8.04844 18.8764C8.19453 18.8865 8.35059 18.9034 8.5 18.9034C12.2387 18.9034 15.3 15.7955 15.3 12C15.3 11.2247 15.2104 10.4528 14.9547 9.7618L16.2297 8.46742ZM8.075 0L11.6609 3.61348L12.2453 4.23371L11.6609 4.85393L8.075 8.46742L6.87969 7.25393L8.95156 5.12359C8.80547 5.11348 8.64941 5.09663 8.5 5.09663C4.76133 5.09663 1.7 8.20449 1.7 12C1.7 12.7753 1.87598 13.5472 2.04531 14.2382L0.770312 15.5326C0.258984 14.4978 0 13.2944 0 12C0 7.25393 3.825 3.37079 8.5 3.37079C8.67265 3.37079 8.83535 3.38764 9.00469 3.39775L6.87969 1.21348L8.075 0Z" fill="white"/>
                </svg>
              </div>
            </button>
            <div className="flex justify-between items-center gap-2 md:gap-4 flex-1 ml-2 md:ml-4">
              {getCurrentQuestions().map((question, index) => (
                <button
                  key={`${currentQuestionSet}-${index}`}
                  onClick={() => handleSuggestionClick(question)}
                  className="flex items-center px-2 md:px-4 py-3 cursor-pointer hover:opacity-80 transition-opacity relative group"
                  title={question}
                  style={{
                    borderRadius: '100px',
                    border: '1px solid #44B8F3',
                    background: 'rgba(5, 15, 38, 0.40)',
                    minWidth: '0',
                    maxWidth: '320px',
                    width: 'calc((100% - 60px) / 3)',
                    overflow: 'hidden'
                  }}
                >
                  <div 
                    className="text-white truncate w-full text-sm md:text-base"
                    style={{
                      fontFamily: 'Elevance Sans, -apple-system, Roboto, Helvetica, sans-serif',
                      fontSize: 'clamp(12px, 2.5vw, 16px)',
                      fontWeight: '400',
                      lineHeight: 'normal'
                    }}
                  >
                    {question}
                  </div>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-normal max-w-xs text-center">
                    {question}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end w-full mt-2">
            <button
              onClick={() => setShowAboutBox(!showAboutBox)}
              className="text-white hover:text-blue-400 transition-colors text-sm underline bg-transparent p-1"
              style={{
                fontFamily: 'Elevance Sans, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: '400',
                minWidth: 'auto',
                whiteSpace: 'nowrap'
              }}
            >
              Learn More About AI
            </button>
          </div>
        </div>
      </div>

      {showAboutBox && (
        <div 
          className="absolute bottom-24 right-8 bg-white rounded-lg shadow-lg p-4 w-80 z-50"
            style={{
              border: '1px solid #e5e7eb',
              marginBottom: '8px'
            }}
          >
            <div className="text-gray-800 text-sm">
              <h3 className="font-semibold mb-2 text-gray-900">About This Application</h3>
              <p className="mb-2">
                This AI-powered assistant helps you with workplace queries, HR policies, and Workday-related questions.
              </p>
              <p className="mb-2">
                Simply type your question or select from the suggested prompts to get started.
              </p>
              <p className="text-xs text-gray-600">
                Powered by advanced AI technology to provide accurate and helpful responses.
              </p>
            </div>
            <div className="absolute bottom-0 right-8 transform translate-y-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
          </div>
        )}
    </div>
  );
};

export default PulseEmbeddedDemo;
